import { useFeatureAccess } from "../../../access-control/hooks/useFeatureAccess";
import { ACCESS_GROUPS, hasAccessGroup } from "../../../access-control/models/access-groups.models";
import { FEATURE_KEYS } from "../../../access-control/models/role-feature.models";
import { useAuthContext } from "../../../auth/state/AuthContext";
import { VesselsRowMenu } from "../../../search-vessels/components/VesselsRowMenu";
import { userAtom } from "../../../shared/atoms";
import { useVesselCardPreference } from "../hooks/useVesselCardPreference";
import { VesselCardPreferenceButtonProps } from "../models/vessel-card-header.models";
import { useAtomValue } from "jotai";
import { useState } from "react";

// Reuses Vessel Search's add, remove, and IMOS-assignment rules for the current card.
// The existing search and My Vessels permissions remain required for those API operations.
const VesselCardPreferenceButton = ({ vessel }: VesselCardPreferenceButtonProps) => {
  const { hasFeature } = useFeatureAccess();
  const accessToken = useAuthContext().state.user?.accessToken;
  const user = useAtomValue(userAtom);
  const fullName = user?.name?.trim() || `${accessToken?.firstname ?? ""} ${accessToken?.lastname ?? ""}`.trim();
  const canManagePreference =
    hasAccessGroup(accessToken?.groups, ACCESS_GROUPS.InternalUsers) &&
    hasFeature(FEATURE_KEYS.CORE_SEARCH_VESSELS) &&
    hasFeature(FEATURE_KEYS.CORE_MY_VESSELS);
  const preference = useVesselCardPreference(vessel, fullName, canManagePreference);
  const [isSaving, setIsSaving] = useState(false);

  if (!canManagePreference || !fullName) {
    return null;
  }

  if (preference.hasError) {
    return (
      <button
        type="button"
        className="text-xs text-green underline dark:text-teal-200"
        data-test="vessel-card-preference-retry-button"
        onClick={preference.retry}
      >
        Retry My Vessels status
      </button>
    );
  }

  return (
    <div
      className="flex min-h-10 min-w-10 items-center justify-center"
      data-test="vessel-card-preference"
      aria-busy={preference.isLoading || isSaving}
    >
      {preference.isLoading && <span className="text-xs text-gray-500 dark:text-gray-300">My Vessels…</span>}
      {!preference.isLoading && !preference.vessel && (
        <span className="text-xs text-gray-500 dark:text-gray-300" data-test="vessel-card-preference-unavailable">
          My Vessels unavailable
        </span>
      )}
      {preference.vessel && (
        <VesselsRowMenu
          fullName={fullName}
          item={preference.vessel}
          setIsLoading={setIsSaving}
          disabled={isSaving}
          tooltipId="vessel-card-preference-tooltip"
        />
      )}
    </div>
  );
};

export default VesselCardPreferenceButton;
